
import { defineComponent, PropType } from 'vue'
import { groupBy } from "lodash";
import { BsIcon, Spinner } from '@hakosalo/hakolib';
import generateQuiteRandomString from "@hakosalo/hakolib";
import texts from '@hakosalo/hakolib';
import { HighlightText } from '@hakosalo/hakolib';
import TagBadge from './TagBadge.vue';
import ISearchSelectOption from '@/interfaces/ISearchSelectOption';
import router from '@/router';
import _ from 'lodash';

export default defineComponent({
	name: "SearchSelect",

	components: {
		BsIcon,
		Spinner,
		TagBadge,
		HighlightText
	},

	emits: [
		"update:modelValue",
		"search"
	],

	props: {
		/** used for v-model */
		modelValue: {
			type: [Array,Object,Number,String,null] as PropType<number[]|string[]|number|string|null>,
			required: true
		},
		/** Selectable options */
		options: {
			type: Array as PropType<ISearchSelectOption<any>[]>,
			required: true
		},
		/** If false, the avatars will be hidden */
		useAvatar: {
			type: Boolean as PropType<boolean>,
			default: false
		},
		/** Set this to true if you need to load options with ajax */
		useAjax: {
			type: Boolean as PropType<boolean>,
			default: false
		},
		/** Input placeholder text */
		placeholder: {
			type: String as PropType<string>,
			default: "Kirjoita jotain hakeaksesi..."
		},
		/** Input ID (for focus) */
		id: {
			type: String as PropType<string>,
			default: () => new Date().toTimeString()
		},
		/** Hide caret (down arrow) */
		hideArrow: {
			type: Boolean as PropType<boolean|undefined>,
			default: undefined
		}
	},

	data() { return {
		showMenu: false,
		hoverOption: null as any|null,
		search: "",
		selectedOptions: [] as string[]|number[],
		singleSelectOptionObjCache: null as ISearchSelectOption<any>|null,
		isLoading: false,

		focusInterval: 0
	}},

	watch: {
		modelValue() {
			if (this.singleSelect) {
				//this.selectedOption = this.modelValue as number|string|null;
				this.singleSelectOptionObjCache = this.options.filter(x => x.value == this.modelValue)[0];
			}
			else {
				this.selectedOptions = this.modelValue as number[]|string[];
			}
		},

		selectedOptions() {
			this.$emit('update:modelValue', this.selectedOptions);
		},

		search() {
			if (this.useAjax && this.search.length > 1) {
				this.isLoading = true;
				this.$emit('search', this.search);
			}
		},

		options() {
			if (this.useAjax) {
				this.isLoading = false;
			}
		},

		optionsFiltered() {
			this.hoverFirstOption();
		},

		showMenu() {
			if (this.showMenu) {
				this.hoverFirstOption();
			}
			else {
				this.search = "";
			}
		}
	},

	beforeMount() {
		if (this.singleSelect && this.modelValue != null) {
			const selectedOption = this.options.filter(x => x.value == this.modelValue)[0];
			if (selectedOption) {
				//this.selectedOption = selectedOption.value;
				this.singleSelectOptionObjCache = selectedOption;
			}
		}
		else if (this.modelValue != null) {
			this.selectedOptions = this.modelValue as any[];
		}

		this.focusInterval = window.setInterval(() => {
			if (!this.showMenu) {
				return;
			}

			let hasFocus = this.checkIfHasFocus();
			if (!hasFocus) {
				this.showMenu = false;
			}
		}, 32);
	},

	beforeUnmount() {
		window.clearInterval(this.focusInterval);
	},

	computed: {
		texts() { return texts; },

		hideArrowInternal() : boolean {
			if (typeof(this.hideArrow) == 'undefined') {
				if (this.useAjax) {
					return true;
				}
				return false;
			}

			return this.hideArrow;
		},

		singleModelValue() : number|string {
			if (Array.isArray(this.modelValue)) {
				throw "singleModelValue cannot be called if the modelValue is an array!";
			}
			return this.modelValue as string|number;
		},

		getSingleModelValueOption() : ISearchSelectOption<any>|null {
			return this.singleSelectOptionObjCache;
		},

		singleSelect() : boolean {
			return !Array.isArray(this.modelValue);
		},

		optionsFiltered() : ISearchSelectOption<any>[] {
			const options = this.options;

			let filteredOptions = options
				.filter(option => {
					if(this.useAjax === true) {
						return true;
					}
					if (option.text.toLowerCase().includes(this.search.toLowerCase())) {
						return true;
					}
					return false;
				});

			return filteredOptions;
		},

		optionsGrouped() : { [key:string]: ISearchSelectOption<any>[] } {
			let groupedOptions = groupBy(this.optionsFiltered, x => x.group);
			return groupedOptions;
		}
	},

	methods: {
		checkIfHasFocus() : boolean {
			let activeElem = document.activeElement;
			if (!activeElem || (activeElem && activeElem.tagName.toLowerCase() == "body")) {
				return false;
			}

			if (this.showMenu == false) {
				return false;
			}

			let rootElem = this.$refs.root as HTMLElement;
			let activeParent = activeElem.parentElement;
			while (activeParent) {
				if (activeParent == rootElem) {
					return true;
				}
				activeParent = activeParent.parentElement;
			}

			return false;
		},

		clickDropdownIcon() {
			this.showMenu = !this.showMenu;
			if (this.showMenu) {
				const searchInput = this.$refs.searchInput as HTMLInputElement;
				searchInput.focus();
			}
		},

		/** Triggered by mouse hovering over option */
		onHoverOption(option:ISearchSelectOption<any>, hoverOn:boolean) {
			this.hoverOption = null;
			if (hoverOn) {
				this.hoverOption = option.value;
			}
		},

		/** "hover" over the first option */
		hoverFirstOption() {
			if (this.optionsFiltered.length > 0) {
				this.hoverOption = this.optionsFiltered[0].value;
			}
		},

		/** Change "hover" to next or previous option */
		hoverNextOrPrevOption(dir:number) {
			if (!this.showMenu) {
				this.showMenu = true;
				return;
			}

			if (!this.hoverOption) {
				this.hoverOption = this.optionsFiltered[0].value;
				return;
			}

			// Check again in case optionsFiltered[0] is undefined
			if (!this.hoverOption) {
				return;
			}

			const indexOfCurrent = this.optionsFiltered.map(x => x.value).indexOf(this.hoverOption);
			const nextHoverOption = this.optionsFiltered[indexOfCurrent + dir];

			if (nextHoverOption) {
				this.hoverOption = nextHoverOption.value;
			}
		},

		selectHoverOption() {
			let option = this.optionsFiltered.filter(x => x.value == this.hoverOption)[0];
			if (option) {
				this.selectOption(option);
			}
		},

		selectOption(option:ISearchSelectOption<any>|undefined|null) {
			if (!option) {
				return;
			}

			let selectedOptions = this.selectedOptions as any[];

			if (Array.isArray(this.selectedOptions) && selectedOptions.includes(option.value)) {
				this.selectedOptions = selectedOptions.filter(x => x != option.value);
				this.$emit('update:modelValue', this.selectedOptions);
			}
			else {
				if (this.singleSelect) {
					this.$emit('update:modelValue', option.value);
					// this.selectedOption = option.value;
					// this.$emit('update:modelValue', this.selectedOption);
				}
				else {
					const arr = this.selectedOptions as any[];
					arr.push(option.value);
					this.selectedOptions = arr.splice(0);
					this.$emit('update:modelValue', this.selectedOptions);
				}
			}
			this.showMenu = false;
		},

		removeSelectedOptions() {
			if (!this.singleSelect) {
				this.$emit('update:modelValue', []);
			}
			else {
				this.$emit('update:modelValue', null);
			}
		},

		getAvatarData(option:ISearchSelectOption<any>) {
			if (option.avatar) {
				return option.avatar;
			}
			return option.text;
		},

		ariaOptionIsSelected(option : ISearchSelectOption<any>) : boolean {
			const selectedOptions = this.selectedOptions as any[];
			if (Array.isArray(selectedOptions)) {
				return selectedOptions.includes(option);
			}
			return option == selectedOptions;
		},

		fakeOptionClasses(option:ISearchSelectOption<any>) : string[] {
			let classes = [] as string[];

			if (this.hoverOption && this.hoverOption == option.value) {
				classes.push("hover");
			} 

			if (this.singleSelect) {
				classes.push("ps-2");
			}

			return classes;
		},

		searchByTag(name: string) {
			router.push(`/search?tag=${name}`)
		},

		sortTags(tags: string[]) {
			return _.orderBy(tags, [tag => tag.toLowerCase()], ['asc']);
		}
	}
});
